import React from 'react';

/* ═══════════════════════════════════════════════════════════════════
   COLOR PALETTES
   Each palette = [light, dark] for the vertical gradient.
   ═══════════════════════════════════════════════════════════════════ */
const PALETTES = {
  blue:    ['#93c5fd', '#2563eb'],
  indigo:  ['#a5b4fc', '#4f46e5'],
  emerald: ['#6ee7b7', '#059669'],
  teal:    ['#5eead4', '#0d9488'],
  amber:   ['#fcd34d', '#d97706'],
  red:     ['#fca5a5', '#dc2626'],
  pink:    ['#f9a8d4', '#db2777'],
  purple:  ['#c4b5fd', '#7c3aed'],
  cyan:    ['#67e8f9', '#0891b2'],
  gray:    ['#d1d5db', '#6b7280'],
};

// Legacy solid-hex → new palette name mapping (backwards-compatible)
const HEX_TO_PALETTE = {
  '#2563eb': 'blue',
  '#10b981': 'emerald',
  '#f59e0b': 'amber',
  '#ef4444': 'red',
  '#8b5cf6': 'purple',
  '#ec4899': 'pink',
  '#06b6d4': 'cyan',
  '#14b8a6': 'teal',
  '#6366f1': 'indigo',
};

function resolvePalette(input) {
  // Named palette
  if (typeof input === 'string' && PALETTES[input]) return PALETTES[input];
  // Legacy hex
  if (typeof input === 'string' && HEX_TO_PALETTE[input]) return PALETTES[HEX_TO_PALETTE[input]];
  // Custom hex — create a lighter version for the gradient top
  if (typeof input === 'string' && input.startsWith('#')) {
    return [lighten(input, 0.35), input];
  }
  // Array of two hexes passed directly
  if (Array.isArray(input) && input.length === 2) return input;
  return PALETTES.blue;
}

// Simple hex lightener
function lighten(hex, amount) {
  const h = hex.replace('#', '');
  const num = parseInt(h.length === 3 ? h.split('').map(c => c + c).join('') : h, 16);
  let r = (num >> 16) & 255;
  let g = (num >> 8) & 255;
  let b = num & 255;
  r = Math.round(r + (255 - r) * amount);
  g = Math.round(g + (255 - g) * amount);
  b = Math.round(b + (255 - b) * amount);
  return `#${[r, g, b].map(x => x.toString(16).padStart(2, '0')).join('')}`;
}

/* ═══════════════════════════════════════════════════════════════════
   BarChart
   ═══════════════════════════════════════════════════════════════════ */
export default function BarChart({
  data = [],
  height = 320,
  color = 'blue',        // 'blue' | 'emerald' | 'amber' | 'red' | 'purple' | ... | '#hex'
  title,
  suffix = '',
}) {
  if (!data.length) {
    return (
      <div className="bg-white p-4 rounded shadow">
        {title && <h3 className="font-semibold mb-3 text-gray-800">{title}</h3>}
        <p className="text-gray-400 text-center py-8">No data</p>
      </div>
    );
  }

  const values = data.map(d => d.value || 0);
  const maxValue = Math.max(...values, 1);
  const allZero = values.every(v => v === 0);
  const defaultPalette = resolvePalette(color);

  return (
    <div className="bg-white p-5 rounded shadow">
      {title && <h3 className="font-semibold mb-4 text-gray-800 text-lg">{title}</h3>}

      {allZero && (
        <p className="text-xs text-gray-400 text-center mb-2">
          <i className="fas fa-info-circle"></i> No data available
        </p>
      )}

      <div className="relative" style={{ height: `${height}px` }}>
        {/* Y-axis labels */}
        <div className="absolute left-0 top-0 bottom-8 w-10 flex flex-col justify-between text-right pr-2">
          {[100, 75, 50, 25, 0].map(percent => (
            <span key={percent} className="text-[10px] text-gray-400 font-medium">
              {Math.round((percent / 100) * maxValue)}{suffix}
            </span>
          ))}
        </div>

        {/* Chart area */}
        <div className="ml-12 h-full flex flex-col">
          {/* Bars container */}
          <div className="flex-1 relative flex items-end justify-around gap-2 border-b border-gray-200">
            {/* Grid lines */}
            <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
              {[0, 25, 50, 75, 100].map(p => (
                <div key={p} className="border-t border-gray-100 w-full" />
              ))}
            </div>

            {/* Bars */}
            {data.map((item, i) => {
              const value = item.value || 0;
              const pct = maxValue > 0 ? (value / maxValue) * 100 : 0;
              const palette = item.color
                ? resolvePalette(item.color)
                : defaultPalette;
              const [light, dark] = value === 0 ? PALETTES.gray : palette;

              return (
                <div key={i} className="flex-1 flex flex-col items-center justify-end h-full relative z-10">
                  {/* Value label */}
                  <span
                    className="text-[11px] font-bold text-gray-800 mb-1 whitespace-nowrap"
                  >
                    {value}{suffix}
                  </span>

                  {/* Bar with gradient + shadow + hover */}
                  <div
                    className="w-full max-w-[60px] rounded-t-md transition-all duration-300 hover:brightness-110 hover:shadow-lg group relative"
                    style={{
                      height: `${Math.max(pct, value === 0 ? 2 : 1)}%`,
                      background: value === 0
                        ? '#e5e7eb'
                        : `linear-gradient(180deg, ${light} 0%, ${dark} 100%)`,
                      minHeight: value === 0 ? '2px' : '6px',
                      boxShadow: value === 0
                        ? 'none'
                        : `0 2px 4px ${dark}33`,
                    }}
                    title={`${item.label}: ${value}${suffix}`}
                  >
                    {/* Subtle highlight at top for depth */}
                    {value > 0 && (
                      <div
                        className="absolute top-0 left-0 right-0 h-1/3 rounded-t-md pointer-events-none"
                        style={{
                          background: 'linear-gradient(180deg, rgba(255,255,255,0.35), rgba(255,255,255,0))',
                        }}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* X-axis labels */}
          <div className="flex justify-around gap-2 pt-2">
            {data.map((item, i) => (
              <div key={i} className="flex-1 text-center">
                <span className="text-xs text-gray-600 font-medium truncate block">
                  {item.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}